import React, { memo } from 'react';

import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { NavigationMenuLink } from '@/components/ui/navigation-menu';
import { useNavNotification } from '@/modules/humanChat/hooks/useNavNotification';
import { MenuItem, NavNotificationKey } from '@/modules/humanChat/models';
import { IconComponent } from '@/modules/humanChat/utils';
import { selectAccountSlug } from '@/redux/session/selectors';

interface Props {
  icon: NonNullable<MenuItem['icon']>;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  link?: string;
  selected: boolean;
  notificationKey?: NavNotificationKey;
  sum?: string;
  tooltipContent?: string;
}

const NotificationDot = memo(({ notificationKey }: { notificationKey: NavNotificationKey }) => {
  const hasNotification = useNavNotification(notificationKey);

  if (!hasNotification) {
    return null;
  }

  return <div className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-destructive" />;
});

NotificationDot.displayName = 'NotificationDot';

const itemClass =
  'h-10 w-16 px-3 py-2.5 hover:bg-nav-hover data-[active=true]:bg-nav-hover data-[active=true]:text-primary';

const ChatNavigationItem = ({
  icon,
  sum,
  notificationKey,
  onClick,
  selected,
  link,
  tooltipContent,
}: Props) => {
  const slug = useSelector(selectAccountSlug);

  const content = (
    <>
      {icon && (
        <div className="relative">
          <IconComponent icon={icon} />
          {notificationKey ? <NotificationDot notificationKey={notificationKey} /> : null}
        </div>
      )}
      {sum && <span className="text-xs leading-4 text-nav-foreground/70">{sum}</span>}
    </>
  );

  return (
    <InfoTooltip content={tooltipContent} side="bottom">
      <span>
        <NavigationMenuLink asChild className={itemClass}>
          {link ? (
            <Link to={`/${slug}${link}`} data-active={selected}>
              {content}
            </Link>
          ) : (
            <button type="button" onClick={onClick} data-active={selected}>
              {content}
            </button>
          )}
        </NavigationMenuLink>
      </span>
    </InfoTooltip>
  );
};
export default memo(ChatNavigationItem);
